import { useEffect } from "react";
import { Button } from "../Buttons/Button";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { Markdown } from "@storybook/blocks";
import SuccessLogo from "../../assets/images/success.svg";
import { getSafeHref } from "../../utils/utils";
import DOMPurify from "dompurify";

/**
 * SuccessModal component for displaying success messages.
 * @param {object} props - Component props.
 * @param {string} props.heading - Button contents.
 * @param {string} props.message - Optional click handler.
 * @param {boolean} props.show - Boolean to control modal visibility.
 * @param {Function} props.handleClose - Function to handle modal close event.
 */

function SuccessModal(props) {
  const { redirect } = props;
  const handleRedirect = () => {
    if (redirect) {
      redirect();
    }
  };

  const safeMessage = DOMPurify.sanitize(props.message);
  useEffect(() => {
    if (!props.show) {
      handleRedirect();
    }
  }, [props.show, redirect]);

  const handleButtonClick = (event) => {
    // Prevent API call if the modal itself is clicked
    if (props.close) {
      event.stopPropagation();
    }
    if (!props?.href) {
      props?.onClick();
    }
  };

  return (
    <Modal
      centered
      className={`p-4 text-center ${
        props?.logo ? "clinical-success-modal" : "success-modal"
      } ${props.wrapperClass}`}
      onHide={props.onHide}
      show={props.show}
    >
      <Modal.Header
        className=" border-0 align-self-center"
        closeButton={props.close}
      >
        <img src={SuccessLogo} alt="Success logo" />
        <Modal.Title className="success-modal-title">
          &nbsp;{props.heading}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Markdown>{safeMessage}</Markdown>
      </Modal.Body>
      <Button
        className={"mb-4 align-self-center btn-primary"}
        href={getSafeHref(props.href)}
        label={props.label}
        onClick={handleButtonClick} // Ensure only this triggers the API call
        type="button"
        variant={props.variant}
      />
    </Modal>
  );
}

SuccessModal.propTypes = {
  heading: PropTypes.string,
  message: PropTypes.string,
  show: PropTypes.bool,
  redirect: PropTypes.func,
  variant: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  label: PropTypes.string,
  href: PropTypes.string,
  close: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  logo: PropTypes.bool,
  wrapperClass: PropTypes.string,
};

SuccessModal.defaultProps = {
  /* Default heading */
  heading: "Success",
  message: "Your account has been created" /* Default message */,
};

export default SuccessModal;
